import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { setAuthToken } from "../lib/api";

/**
 * Auth context — single source of truth for who is logged in.
 *
 * Migration path:
 *  • Today: token lives in sessionStorage (XSS-readable) — legacy LoginPage
 *    still writes it there. We READ it on boot, but ALSO call /v1/me with
 *    `credentials: "include"` so that once the backend issues an httpOnly
 *    cookie, the cookie wins automatically.
 *  • Tomorrow: drop sessionStorage entirely; user is identified purely by
 *    the cookie + /v1/me round trip.
 *
 * Provides:
 *  • user             (id, name, role, ps_code, rank)
 *  • status           ("loading" | "authenticated" | "anonymous")
 *  • hasRole(role)
 *  • hasAnyRole([…])
 *  • signOut()
 */

export interface AuthUser {
  id: string;
  name: string;
  role: string;
  roles?: string[];          // multi-role support
  rank?: string;
  ps_code?: string;
  permissions?: string[];
}

export type AuthStatus = "loading" | "authenticated" | "anonymous";

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (perm: string) => boolean;
  refresh: () => Promise<void>;
  signOut: (reason?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SS_KEY = "mhpol.auth";

function readLegacySession(): { token?: string; ps?: string } | null {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");

  const refresh = useCallback(async () => {
    // 1) Bootstrap legacy session-storage token into the API client (transitional).
    const legacy = readLegacySession();
    if (legacy?.token) setAuthToken(legacy.token, legacy.ps ?? null);

    // 2) Confirm identity against backend (cookie OR header auth, either works).
    try {
      const headers: Record<string, string> = {};
      if (legacy?.token) {
        headers["Authorization"] = legacy.token.startsWith("Bearer ")
          ? legacy.token : `Bearer ${legacy.token}`;
      }
      const r = await fetch("/v1/me", { credentials: "include", headers });
      if (r.ok) {
        const u: AuthUser = await r.json();
        setUser(u);
        setStatus("authenticated");
        return;
      }
      // 401 / 403 → not signed in
      setUser(null);
      setStatus("anonymous");
    } catch {
      // Network error: keep last-known good user (offline gracefully).
      if (!user) setStatus("anonymous");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const signOut = useCallback(async (reason?: string) => {
    try { await fetch("/v1/auth/logout", { method: "POST", credentials: "include" }); }
    catch { /* best-effort */ }
    try { sessionStorage.removeItem(SS_KEY); } catch { /* noop */ }
    setAuthToken(null, null);
    setUser(null);
    setStatus("anonymous");
    const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
    window.location.assign(`/login${qs}`);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user, status, refresh, signOut,
    hasRole:       (role)  => !!user && (user.role === role || user.roles?.includes(role) === true),
    hasAnyRole:    (roles) => !!user && roles.some((r) => user.role === r || user.roles?.includes(r) === true),
    hasPermission: (p)     => !!user?.permissions?.includes(p),
  }), [user, status, refresh, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

/** Loose helper for code that runs outside React tree. */
export function tryGetLegacyToken(): string | null {
  return readLegacySession()?.token ?? null;
}
